<template>
  <div>
    <v-card class="pa-6" v-if="!viewAuditDasboard">
      <v-row>
        <v-col xs="12" md="12">
          <v-alert
            outlined
            type="warning"
            prominent
            border="top"
            icon="mdi-alert"
            class="mx-auto"
            transition="scale-transition"
          >
            You don't have sufficient previleges to access this page or resource,
            please contact the system administrator.
          </v-alert>
        </v-col>
      </v-row>
    </v-card>

    <div v-else>
      <!-- KPI tiles -->
      <div class="vms-dash-kpi">
        <div v-for="(stat, s) in stats" :key="s" class="vms-insight-card">
          <div class="vms-insight-card__label">
            <v-icon small :color="stat.color" class="mr-1">{{ stat.icon }}</v-icon>
            {{ stat.label }}
          </div>
          <div class="vms-insight-card__value">{{ stat.number }}</div>
        </div>
      </div>

      <!-- Insight strip, computed from loaded chart series -->
      <div
        class="vms-insight-strip"
        v-if="busiestMonth || topOrganization || peakGuestMonth"
      >
        <div class="vms-insight-card" v-if="busiestMonth">
          <div class="vms-insight-card__label">Busiest Month</div>
          <div class="vms-insight-card__value">{{ busiestMonth.label }}</div>
          <div class="vms-insight-card__hint">{{ busiestMonth.value }} visits recorded</div>
        </div>

        <div class="vms-insight-card" v-if="topOrganization">
          <div class="vms-insight-card__label">Top Organization</div>
          <div class="vms-insight-card__value">{{ shortOrgName(topOrganization.label) }}</div>
          <div class="vms-insight-card__hint">{{ topOrganization.value }} visits hosted</div>
        </div>

        <div class="vms-insight-card" v-if="peakGuestMonth">
          <div class="vms-insight-card__label">Peak Guest Month</div>
          <div class="vms-insight-card__value">{{ peakGuestMonth.label }}</div>
          <div class="vms-insight-card__hint">{{ peakGuestMonth.value }} guests registered</div>
        </div>
      </div>

      <!-- Charts -->
      <div class="vms-chart-grid">
        <div class="vms-chart-span-6">
          <v-card class="vms-surface" elevation="0" v-if="bar_chart_is_ready">
            <v-card-title class="vms-page-title" style="font-size: 1.05rem">
              Visits per Month
            </v-card-title>
            <v-card-text>
              <GChart
                :settings="{ packages: ['bar'] }"
                :data="published"
                :options="publishedOptions"
                :createChart="(el, google) => new google.charts.Bar(el)"
              />
            </v-card-text>
          </v-card>

          <v-card class="vms-surface" elevation="0" v-else>
            <v-card-text>
              <v-row align="center" justify="center" class="text-center">
                <v-col>
                  <v-progress-circular
                    indeterminate
                    color="error"
                    class="my-3"
                  ></v-progress-circular>
                  <p caption>Visits per Month Loading... Please wait</p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <div class="vms-chart-span-6">
          <v-card class="vms-surface" elevation="0" v-if="pie_chart_is_ready">
            <v-card-title class="vms-page-title" style="font-size: 1.05rem">
              Visits by Organization
            </v-card-title>
            <v-card-text>
              <GChart
                type="PieChart"
                loading="true"
                :data="events_by_organization"
                :options="poChartOptions2"
              />
            </v-card-text>
          </v-card>

          <v-card class="vms-surface" elevation="0" v-else>
            <v-card-text>
              <v-row align="center" justify="center" class="text-center">
                <v-col>
                  <v-progress-circular
                    indeterminate
                    color="error"
                    class="my-3"
                  ></v-progress-circular>
                  <p caption>Visits by Organization Loading... Please wait</p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <div class="vms-chart-span-6">
          <v-card class="vms-surface" elevation="0" v-if="line_chart_is_ready">
            <v-card-title class="vms-page-title" style="font-size: 1.05rem">
              Guests by Month
            </v-card-title>
            <v-card-text>
              <GChart
                :settings="{ packages: ['line'] }"
                :data="bids"
                :options="bidsOptions"
                :createChart="(el, google) => new google.charts.Line(el)"
                :events="chartEvents"
              />
            </v-card-text>
          </v-card>

          <v-card class="vms-surface" elevation="0" v-else>
            <v-card-text>
              <v-row align="center" justify="center" class="text-center">
                <v-col>
                  <v-progress-circular
                    indeterminate
                    color="error"
                    class="my-3"
                  ></v-progress-circular>
                  <p caption>Guests by Month Loading... Please wait</p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <div class="vms-chart-span-6">
          <v-card class="vms-surface" elevation="0" v-if="bidders_pie_chart_is_ready">
            <v-card-title class="vms-page-title" style="font-size: 1.05rem">
              Visit Titles
            </v-card-title>
            <v-card-text>
              <GChart
                type="PieChart"
                loading="true"
                :data="bidders_by_percentage"
                :options="bidsPieChartOptions"
              />
            </v-card-text>
          </v-card>

          <v-card class="vms-surface" elevation="0" v-else>
            <v-card-text>
              <v-row align="center" justify="center" class="text-center">
                <v-col>
                  <v-progress-circular
                    indeterminate
                    color="error"
                    class="my-3"
                  ></v-progress-circular>
                  <p caption>Visit Titles Loading... Please wait</p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <div class="vms-chart-span-6">
          <v-card class="vms-surface" elevation="0" v-if="top_5_bar_chart_is_ready">
            <v-card-title class="vms-page-title" style="font-size: 1.05rem">
              Top Weekly Visitors
            </v-card-title>
            <v-card-text>
              <GChart
                :settings="{ packages: ['bar'] }"
                :data="top5Visitors"
                :options="top5Options"
                :createChart="(el, google) => new google.charts.Bar(el)"
              />
            </v-card-text>
          </v-card>

          <v-card class="vms-surface" elevation="0" v-else>
            <v-card-text>
              <v-row align="center" justify="center" class="text-center">
                <v-col>
                  <v-progress-circular
                    indeterminate
                    color="error"
                    class="my-3"
                  ></v-progress-circular>
                  <p caption>Top Weekly Visitors Loading... Please wait</p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <div class="vms-chart-span-6">
          <v-card class="vms-surface" elevation="0" v-if="top_longest_staying_bar_chart_is_ready">
            <v-card-title class="vms-page-title" style="font-size: 1.05rem">
              Longest Stays
            </v-card-title>
            <v-card-text>
              <GChart
                :settings="{ packages: ['bar'] }"
                :data="topLongestStayingVisitors"
                :options="topLongestStayingOptions"
                :createChart="(el, google) => new google.charts.Bar(el)"
              />
            </v-card-text>
          </v-card>

          <v-card class="vms-surface" elevation="0" v-else>
            <v-card-text>
              <v-row align="center" justify="center" class="text-center">
                <v-col>
                  <v-progress-circular
                    indeterminate
                    color="error"
                    class="my-3"
                  ></v-progress-circular>
                  <p caption>Longest Stays Loading... Please wait</p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DashboardService from "../../services/dashboard.service";
import UserService from "../../services/user.service";

export default {
  computed: {
    loggedIn() {
      return this.$store.state.auth.status.loggedIn;
    },
    loading() {
      return this.$store.state.loading;
    },
    user() {
      return this.$store.state.auth.user;
    },
    org() {
      return JSON.parse(localStorage.getItem("org"));
    },
    viewAuditDasboard() {
      if (this.org) {
        return true;
      } else {
        return false;
      }
    },

    busiestMonth() {
      return this.topSeriesEntry(this.published, this.bar_chart_is_ready);
    },
    topOrganization() {
      return this.topSeriesEntry(this.events_by_organization, this.pie_chart_is_ready);
    },
    peakGuestMonth() {
      return this.topSeriesEntry(this.bids, this.line_chart_is_ready);
    },
  },

  created() {
    if (!this.loggedIn) {
      this.$router.push("/login");
    }
    this.getTotalVisits();
    this.getTotalBedsVisited();
    this.getGuestsCount();
    this.getHostsCount();
    this.getTopVisitors();
    this.getWardsVisitSummary();
    this.getEventsByOrganization();
    this.getAllGuestsByMonth();
    this.getTop5Visitors();
    this.getTop5LongestStayingVisitors();
  },

  data() {
    return {
      e1: 0,
      dialog: true,
      pageReady: false,
      bar_chart_is_ready: false,
      top_5_bar_chart_is_ready: false,
      top_longest_staying_bar_chart_is_ready: false,
      pie_chart_is_ready: false,
      bidders_pie_chart_is_ready: false,
      line_chart_is_ready: false,
      column_chart_is_ready: false,
      stats: [
        {
          number: "0",
          label: "Total Visits",
          loading: false,
          description: "10% since last week",
          color: "error",
          icon: "mdi-calendar-text-outline",
          href: "#",
        },
        {
          number: "0",
          label: "Hosts",
          loading: false,
          description: "15% since last week",
          color: "green",
          icon: "mdi-account-tie-outline",
          href: "#",
        },
        {
          number: "0",
          label: "Guests",
          loading: false,
          description: "40% of monthly total expected",
          color: "blue",
          icon: "mdi-account-group-outline",
          href: "#",
        },
        {
          number: "0",
          label: "System Users",
          loading: false,
          description: "5% of total this month",
          color: "grey",
          icon: "mdi-account-multiple",
          href: "#",
        },
      ],
      // Array will be automatically processed with visualization.arrayToDataTable function

      poChartOptions: {
        title: "Visits by Organization",
        is3D: false,
        pieHole: 0.4,
        legend: {
          position: "bottom",
        },
      },
      poChartOptions2: {
        title: "Visits by Organization",
        subtitle: "All visits grouped by host organization",
        is3D: false,
        pieHole: 0.4,
        legend: {
          position: "bottom",
        },
      },
      bids: [
        ["Month", "Guests"],
        ["Jan", 0],
        ["Feb", 0],
        ["Mar", 0],
        ["Apr", 0],
        ["May", 0],
        ["Jun", 0],
        ["Jul", 0],
        ["Aug", 0],
        ["Sep", 0],
        ["Oct", 0],
        ["Nov", 0],
        ["Dec", 0],
      ],
      bidsOptions: {
        chart: {
          title: "Guests by Month",
          subtitle: "All registered guests per month",
          is3D: false,
          pieHole: 0.4,
        },
      },
      published: [
        ["Month", "Visits"],
        ["Jan", 0],
        ["Feb", 0],
        ["Mar", 0],
        ["Apr", 0],
        ["May", 0],
        ["Jun", 0],
        ["Jul", 0],
        ["Aug", 0],
        ["Sep", 0],
        ["Oct", 0],
        ["Nov", 0],
        ["Dec", 0],
      ],

      top5Visitors: [
        ["Name", "Visits"],
        ["Jane Doe", 0],
        ["John Doe", 0]
      ],

      topLongestStayingVisitors: [
        ["Name", "Time Spent"],
        ["Jane Doe", 0],
        ["John Doe", 0]
      ],

      publishedOptions: {
        chart: {
          title: "Visits per Month " + new Date().getFullYear(),
          subtitle: "All recorded visits by month",
          color: "#76A7FA",
          is3D: true,
          pieHole: 0.4,
        },
      },

      top5Options: {
        chart: {
          title: "Top Weekly Visitors " + new Date().getFullYear(),
          subtitle: "Top 5 Visitors (Last 7 Days)",
          color: "#8C9EFF",
          is3D: true,
          pieHole: 0.4,
        },
      },

      topLongestStayingOptions: {
        chart: {
          title: "Longest Stays " + new Date().getFullYear(),
          subtitle: "Top 5 Longest Staying Visitors (Last 7 Days)",
          color: "#BOBE65",
          is3D: true,
          pieHole: 0.4,
        },
      },

      chartData: [
        ["Month", "Users"],
        ["Jan", 0],
        ["Feb", 0],
        ["Mar", 0],
        ["Apr", 0],
        ["May", 0],
        ["Jun", 0],
        ["Jul", 0],
        ["Aug", 0],
        ["Sep", 0],
        ["Oct", 0],
        ["Nov", 0],
        ["Dec", 0],
      ],

      chartEvents: {
        ready: () => {
          // do something when chart is first drawn
        },
      },

      usersChartOptions: {
        chart: {
          title: "System Users",
          subtitle: "Bidders, Administrators, and Companies: 2021",
          color: "#97d1fa",
          is3D: false,
          pieHole: 0.4,
        },
      },

      events_by_organization: [
        ["Visits Category", "Percentage"],
        ["Open Tender", 0],
        ["Prequalification", 0],
        ["Restricted Tender", 0],
        ["Expression of Interest", 0],
        ["Direct Procurement", 0],
        ["Request for Quotation", 0],
        ["Single-stage and two-stage tender", 0],
      ],

      list_of_latest_tenders: [],
      bidders_by_percentage: [
        ["Visits Category", "Percentage"],
        ["Approved", 0],
        ["Pending", 0],
        ["Rejected", 0],
      ],

      monthly_users_by_role: [],

      suppliersChartOptions: {
        chart: {
          title: "Number of Suppliers By Supplier Category",
          subtitle: "Suppliers: 2021",
        },
        bars: "horizontal",
        hAxis: { format: "decimal" },
        height: 1000,
        chartArea: {
          width: "100%",
          height: "100%",
        },
        colors: ["#77ba99", "#ef9e44", "#97d1fa", "#a45f6e", "#d14f57"],
      },

      picker: new Date().toISOString().substr(0, 10),

      bidsPieChartData: [
        ["Visits Category", "Percentage"],
        ["Approved", 0],
        ["Pending", 0],
        ["Rejected", 0],
      ],

      bidsPieChartOptions: {
        title: "Visit Titles",
        subtitle: "Official, unofficial, and undefined visits",
        is3D: false,
        legend: {
          position: "bottom",
        },
      },

      items: [],
    };
  },

  methods: {
    onChartReady() {},
    getCompanyName(company) {
      return company.company_name;
    },

    clarificationPreview(item) {
      this.$router.push("/clarification/" + item.id);
    },

    /**
     * From a GChart-ready series (first row is the header),
     * return the {label, value} pair with the highest value.
     */
    shortOrgName(name) {
      if (name == null || name === '') return '';
      return String(name).split('@')[0].trim() || String(name);
    },

    topSeriesEntry(series, isReady) {
      if (!isReady || !Array.isArray(series) || series.length < 2) {
        return null;
      }

      const rows = series
        .slice(1)
        .filter((row) => Array.isArray(row) && row.length >= 2)
        .map((row) => [this.shortOrgName(row[0]), Number(row[1])])
        .filter((row) => !Number.isNaN(row[1]));

      if (!rows.length) {
        return null;
      }

      const top = rows.reduce((best, row) => (row[1] > best[1] ? row : best));

      if (top[1] <= 0) {
        return null;
      }

      return { label: top[0], value: top[1] };
    },

    normalizeOrgSeries(obj) {
      const entries = Object.entries(obj || {})
        .map(([name, value]) => [this.shortOrgName(name), Number(value)])
        .filter((row) => row[0] && !Number.isNaN(row[1]));
      // GChart + topSeriesEntry expect a header row first
      return entries.length
        ? [["Organization", "Visits"], ...entries]
        : [];
    },

    getEventsByOrganization() {
      return DashboardService.getEventsByOrganization().then(
        (response) => {
          if (response.status == 200) {
            let obj = response.data.data;
            this.events_by_organization = this.normalizeOrgSeries(obj);
            this.pie_chart_is_ready = true;
          } else {
            this.events_by_organization = [];
          }
        },
        (error) => {
          this.items = [];
          console.log(error);
        }
      );
    },

    getTopVisitors() {
      return DashboardService.getTopVisitors().then(
        (response) => {
          if (response.status == 200) {
            this.published = Object.entries(response.data.data);
            this.bar_chart_is_ready = true;
          } else {
            console.log(response.data.error);
          }
        },
        (error) => {
          this.items = [];
          console.log(error);
        }
      );
    },

    getTop5Visitors() {
      return DashboardService.getTop5Visitors().then(
        (response) => {
          if (response.status == 200) {
            this.top5Visitors = Object.entries(response.data.data);
            this.top_5_bar_chart_is_ready = true;
          } else {
            console.log(response.data.error);
          }
        },
        (error) => {
          this.items = [];
          console.log(error);
        }
      );
    },

    getTop5LongestStayingVisitors() {
      return DashboardService.getTop5LongestStayingVisitors().then(
        (response) => {
          if (response.status == 200) {
            this.topLongestStayingVisitors = Object.entries(response.data.data);
            this.top_longest_staying_bar_chart_is_ready = true;
          } else {
            console.log(response.data.error);
          }
        },
        (error) => {
          this.items = [];
          console.log(error);
        }
      );
    },

    getAllTenders() {
      return DashboardService.getAllTenders().then(
        (response) => {
          if (response.status == 200) {
            this.list_of_latest_tenders = response.data.data;
            console.log(this.list_of_latest_tenders);
          } else {
            this.list_of_latest_tenders = [];
            console.log(response.data.error);
          }
        },
        (error) => {
          this.items = [];
          console.log(error);
        }
      );
    },

    tenderPreview(item) {
      this.$router.push("/tender-preview/" + item.id);
    },

    getGuestsCount() {
      return DashboardService.getGuestsCount().then(
        (response) => {
          if (response.status == 200) {
            this.stats[2].number = response.data.data.toString();
            this.stats[2].loading = true;
          } else {
            this.stats[2].number = 0;
            console.log(response);
          }
        },
        (error) => {
          this.items = [];
          console.log(error);
        }
      );
    },

    getHostsCount() {
      return DashboardService.getHostsCount().then(
        (response) => {
          if (response.status == 200) {
            this.stats[3].number = response.data.data.toString();
            this.stats[3].loading = true;
          } else {
            this.stats[3].number = 0;
            console.log(response);
          }
        },
        (error) => {
          this.stats[3].number = [];
          console.log(error);
        }
      );
    },

    getTotalBedsVisited() {
      return DashboardService.getTotalBedsVisited().then(
        (response) => {
          if (response.status == 200) {
            this.stats[1].number = response.data.data.toString();
            this.stats[1].loading = true;
          } else {
            this.stats[1].number = 0;
            console.log(response);
          }
        },
        (error) => {
          this.stats[2].number = [];
          console.log(error);
        }
      );
    },

    getTotalVisits() {
      return DashboardService.getTotalVisits().then(
        (response) => {
          if (response.status == 200) {
            this.stats[0].number = response.data.data.toString();
            this.stats[0].loading = true;
          } else {
            this.stats[0].number = 0;
            console.log(response);
          }
        },
        (error) => {
          this.items = [];
          console.log(error);
        }
      );
    },

    getAllGuestsByMonth() {
      return DashboardService.getAllGuestsByMonth().then(
        (response) => {
          if (response.status == 200) {
            this.bids = Object.entries(response.data.data);
            this.line_chart_is_ready = true;
          } else {
            console.log(response.data.error);
          }
        },
        (error) => {
          this.bids = [];
          console.log(error);
        }
      );
    },

    getWardsVisitSummary() {
      return DashboardService.getWardsVisitSummary().then(
        (response) => {
          if (response.status == 200) {
            let obj = response.data.data;
            this.bidders_by_percentage = Object.entries(obj);
            this.bidders_pie_chart_is_ready = true;
          } else {
            this.bidders_by_percentage = [];
          }
        },
        (error) => {
          this.items = [];
          console.log(error);
        }
      );
    },

    getAllMonthlyUsersByRole() {
      return UserService.getAllMonthlyUsersByRole().then(
        (response) => {
          console.log(response);
          if (response.status == 200) {
            let obj = response.data.data;
            this.chartData = Object.entries(obj);
            console.log(response.data.data);
            this.column_chart_is_ready = true;
          } else {
            this.bidders_by_percentage = [];
            console.log(response);
          }
        },
        (error) => {
          this.items = [];
          console.log(error);
        }
      );
    },
  },
};
</script>
